import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useSearchParams } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { Bento } from "@/components/ui/box";
import { ApiError } from "@/lib/api/client";
import { inboxApi } from "@/lib/api/inbox";
import { connectionsBox } from "./connections-box";
import { conversationsBox } from "./conversations-box";
import { useConversationReplies, type ReplyTransport } from "./use-conversation-replies";
import { useNewMessageBox, type NewMessageTransport } from "./new-message-box";
import { useInboxTransports } from "./inbox-send";
import { inboxRead } from "./inbox-read";
import { inboxSelection } from "./inbox-selection";
import { InboxRefusal } from "./inbox-refusal";
import { inboxWords as words } from "./inbox-copy";

type Transports={replyTransport?:ReplyTransport;newTransport?:NewMessageTransport};
export function InboxWorkspace(props:Transports) {
  const [params]=useSearchParams();
  if(inboxSelection(params).invalid)return <InboxRefusal />;
  return <Workspace {...props} />;
}
function Workspace(props:Transports) {
  const transports=useInboxTransports();
  const replyTransport=props.replyTransport??transports.reply;
  const newTransport=props.newTransport??transports.compose;
  const {me}=useAuth();
  const client=useQueryClient();
  const location=useLocation();
  const [params,setParams]=useSearchParams();
  const selection=inboxSelection(params);
  const [query,setQuery]=useState("");
  const key=useMemo(()=>["inbox",me?.tenant_id,me?.user_id],[me?.tenant_id,me?.user_id]);
  const context=useQuery({queryKey:[...key,"context",""],queryFn:()=>inboxApi.context(),enabled:Boolean(me),retry:false});
  const ranking=useQuery({queryKey:[...key,"ranking",selection.course],queryFn:()=>inboxApi.ranking(selection.course||undefined),enabled:Boolean(me),retry:false});
  // Opening a row re-reads the list for that conversation. The rows on screen stay drawn
  // meanwhile, so the opened row keeps focus (P5); only this login's own rows carry over.
  const list=useQuery({queryKey:[...key,"list",selection.conversation],queryFn:()=>inboxApi.list(undefined,selection.conversation||undefined),enabled:Boolean(me),retry:false,
    placeholderData:(previous,previousQuery)=>previousQuery?.queryKey[1]===key[1]&&previousQuery?.queryKey[2]===key[2]?previous:undefined});
  const refresh=useMutation({mutationFn:()=>inboxApi.refreshAll(),onSuccess:()=>client.invalidateQueries({queryKey:[...key,"list"]})});
  const refreshOnce=refresh.mutate;
  const opened=useRef<string|null>(null);
  const actor=JSON.stringify([me?.tenant_id,me?.user_id]);
  const active=Object.values(context.data?.channels??{}).some(connection=>connection.status==="active");
  useEffect(()=>{
    if(!active||opened.current===actor)return;
    opened.current=actor;
    refreshOnce();
  },[actor,active,refreshOnce]);
  const reread=context.refetch;
  useEffect(()=>{
    const returned=()=>{if(document.visibilityState==="visible")void reread();};
    window.addEventListener("focus",returned);
    document.addEventListener("visibilitychange",returned);
    return ()=>{window.removeEventListener("focus",returned);document.removeEventListener("visibilitychange",returned);};
  },[reread]);
  const preview={email:context.data?.channels.email.mode==="preview",chat:context.data?.channels.chat.mode==="preview"};
  const replies=useConversationReplies(replyTransport,preview);
  const compose=useNewMessageBox({course:selection.course,recipient:selection.recipient,session:selection.session,transport:newTransport});
  const attempts=useRef(new Set<string>());
  const [marks,setMarks]=useState<Record<string,{failed:boolean;pending:boolean}>>({});
  const rows=useRef(list.data?.conversations);
  rows.current=list.data?.conversations;
  const markRead=useCallback((id:string,retry=false)=>{
    const identity=`${actor}/${id}`;
    if((attempts.current.has(identity)&&!retry)||!rows.current?.find(row=>row.id===id)?.unread)return;
    attempts.current.add(identity);
    setMarks(was=>({...was,[id]:{failed:false,pending:true}}));
    void inboxApi.read(id).then(()=>{
      setMarks(was=>({...was,[id]:{failed:false,pending:false}}));
      void client.invalidateQueries({queryKey:[...key,"list"]});
    }).catch(()=>setMarks(was=>({...was,[id]:{failed:true,pending:false}})));
  },[actor,client,key]);
  const available=Boolean(list.data);
  useEffect(()=>{if(selection.conversation&&available)markRead(selection.conversation);},[selection.conversation,available,markRead]);
  const selected=list.data?.conversations.find(row=>row.id===selection.conversation);
  const refused=Boolean(list.isSuccess&&!list.isPlaceholderData&&selection.conversation&&!selected)
    || Boolean(selected&&selection.course&&selected.course_id!==selection.course)
    || Boolean(selection.conversation&&list.error instanceof ApiError&&[403,404,422].includes(list.error.status));
  if(refused)return <InboxRefusal />;
  const change=(patch:Record<string,string>)=>{
    const next=new URLSearchParams(params);
    for(const [name,value] of Object.entries(patch)){if(value)next.set(name,value);else next.delete(name);}
    setParams(next);
  };
  const conversations=conversationsBox({
    read:inboxRead(list,words.conversationsFailed),actor:me?.user_id??"",query,onQueryChange:setQuery,
    selected:selection.conversation,draft:false,task:replies.task,
    mark:id=>({...marks[id]??{failed:false,pending:false},retry:()=>markRead(id,true)}),
    course:{label:words.course,value:selection.course,options:[{value:"",label:words.allCourses},...(context.data?.courses??[]).map(course=>({value:course.id,label:course.title})),
      ...(selection.course&&!context.data?.courses.some(course=>course.id===selection.course)?[{value:selection.course,label:words.selectedCourse}]:[])],onChange:course=>change({course,conversation:""})},
    channel:{label:words.channel,value:selection.channel,options:[{value:"all",label:words.allChannels},{value:"email",label:words.email},{value:"chat",label:words.chat}],onChange:channel=>change({channel:channel==="all"?"":channel,conversation:""})},
  });
  const boxes=[connectionsBox(inboxRead(context,words.connectionsFailed)),compose.box,conversations].map(box=>{
    const rank=!ranking.isError&&ranking.data?.state==="ready"?ranking.data.value?.candidates.find(candidate=>candidate.id===box.id):undefined;
    return rank?{...box,candidate:{eligible:rank.eligible,blocked:null,rank:rank.rank}}:box;
  });
  return <div className="app-page" data-page="course-inbox">
    <header data-slot="page-frame" className="flex flex-wrap items-end justify-between gap-4">
      <h1 className="text-title font-semibold">{words.inbox}</h1>
      <Link className="text-ui font-semibold text-primary" to={`${location.pathname}?view=notices`}>{words.notices}</Link>
    </header>
    <Bento id="course-inbox" journey="inbox" boxes={boxes}
      defaultOpen={selection.compose?["new-message"]:[]} defaultIntent={selection.compose?"new-message":null}
      onOpenChange={(id,open)=>{if(id==="new-message"&&open)compose.reset();}}
      onRowOpenChange={(id,row,open)=>{if(id==="conversations"){
        change({conversation:open?row:"",compose:"",recipient:"",session:""});
        if(open)markRead(row);else attempts.current.delete(`${actor}/${row}`);
      }}} />
  </div>;
}
